import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ListEmpty from "../GeneralComponents/ListEmpty";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";

const METRICS = [
  { key: "trafico_subida",               label: "Tráfico promedio de subida" },
  { key: "trafico_bajada",               label: "Tráfico promedio de bajada" },
  { key: "trafico_bajada_internacional", label: "Tráfico promedio de bajada internacional" },
  { key: "latencia",                     label: "Latencia" },
  { key: "perdida_paquete",              label: "Perdida de paquete" },
];

const TIME_FILTERS = [
  { key: "menos30",  label: "menos de 30 días",  test: (d) => d < 30 },
  { key: "sobre30",  label: "Sobre 30 días",      test: (d) => d >= 30 },
  { key: "sobre60",  label: "Sobre 60 días",      test: (d) => d >= 60 },
  { key: "sobre180", label: "Sobre 180 días o +", test: (d) => d >= 180 },
];

const SALMON = "#F4BBAA";
const GROUP_BG = "rgba(25, 118, 210, 0.10)";

function groupRows(rows, enabledKeys) {
  if (!enabledKeys.length) return [{ id: "__all__", partsMap: {}, rows }];
  const map = new Map();
  for (const r of rows) {
    const key = enabledKeys
      .map((k) => `${k}:${r?.[k] == null ? "N/A" : String(r[k])}`)
      .join("|");
    if (!map.has(key)) {
      const partsMap = {};
      enabledKeys.forEach((k) => (partsMap[k] = r?.[k] ?? null));
      map.set(key, { id: key, partsMap, rows: [] });
    }
    map.get(key).rows.push(r);
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => a.id.localeCompare(b.id, "es", { numeric: true }));
  return groups;
}

function buildGroupTitle(keys, partsMap) {
  const label = { operador: "Operador", zona: "Zona" };
  return keys
    .map((k) => `${label[k] ?? k}: ${partsMap[k] == null ? "N/A" : partsMap[k]}`)
    .join(" - ");
}

function calcDias(primer_dia_cero) {
  if (!primer_dia_cero) return 0;
  return dayjs().diff(dayjs(primer_dia_cero), "day");
}

function tiempoStr(dias) {
  if (dias === 0) return "hoy";
  if (dias === 1) return "hace 1 día";
  return `hace ${dias} días`;
}

const COLS = [
  { key: "rbd",          header: "RBD" },
  { key: "zona",         header: "Zona" },
  { key: "operador",     header: "Operador" },
  { key: "convocatoria", header: "Convocatoria" },
  { key: "nombre",       header: "Nombre" },
  { key: "_tiempo",      header: "Tiempo" },
  { key: "primer_dia_cero", header: "Desde" },
];

function DataTable({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, showPagination }) {
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLS.map((c) => (
                <TableCell key={c.key} sx={{ fontWeight: 600 }}>{c.header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLS.length}><ListEmpty /></TableCell>
              </TableRow>
            ) : (
              rows.map((r, idx) => (
                <TableRow key={`${r.rbd}-${idx}`} hover>
                  <TableCell>{r.rbd}</TableCell>
                  <TableCell>{r.zona ?? ""}</TableCell>
                  <TableCell>{r.operador ?? ""}</TableCell>
                  <TableCell>{r.convocatoria ?? ""}</TableCell>
                  <TableCell>{r.nombre ?? ""}</TableCell>
                  <TableCell>{r._tiempo}</TableCell>
                  <TableCell>{r.primer_dia_cero}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <TablePagination
          component="div"
          count={totalCount ?? 0}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
        />
      )}
    </>
  );
}

export default function RbdSinMovimiento() {
  const { setPageTitle } = useContext(GeneralContext);

  const today = dayjs().format("YYYY-MM-DD");
  const firstOfYear = dayjs().startOf("year").format("YYYY-MM-DD");

  const [fechaInicio, setFechaInicio] = useState(firstOfYear);
  const [fechaFin, setFechaFin] = useState(today);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [grouping, setGrouping] = useState({ operador: false, zona: false });
  const [tiempoActivo, setTiempoActivo] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    setPageTitle?.("RBD SIN MOVIMIENTO DE TRÁFICO");
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const resp = await axios.getRbdSinMovimiento({ fecha_inicio: fechaInicio, fecha_fin: fechaFin });
        setApiData(resp?.data ?? null);
      } catch {
        setApiData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fechaInicio, fechaFin]);

  const metricas = apiData?.metricas ?? {};
  const totalRbd = apiData?.total ?? 0;

  const rawDatos = useMemo(() => {
    const list = apiData?.data ?? [];
    return list.map((r) => {
      const _dias = calcDias(r.primer_dia_cero);
      return { ...r, _dias, _tiempo: tiempoStr(_dias) };
    });
  }, [apiData]);

  const filteredDatos = useMemo(() => {
    if (!tiempoActivo) return rawDatos;
    const filtro = TIME_FILTERS.find((f) => f.key === tiempoActivo);
    if (!filtro) return rawDatos;
    return rawDatos.filter((r) => filtro.test(r._dias));
  }, [rawDatos, tiempoActivo]);

  const enabledGroupKeys = useMemo(() => {
    const keys = [];
    if (grouping.operador) keys.push("operador");
    if (grouping.zona) keys.push("zona");
    return keys;
  }, [grouping]);

  const isGrouped = enabledGroupKeys.length > 0;

  const pagedDatos = useMemo(
    () => filteredDatos.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredDatos, page, rowsPerPage]
  );

  const groups = useMemo(
    () => groupRows(filteredDatos, enabledGroupKeys),
    [filteredDatos, enabledGroupKeys]
  );

  const handleDownloadCSV = () => {
    const headers = COLS.map((c) => c.header);
    const keys = COLS.map((c) => c.key);
    const rows = filteredDatos.map((r) =>
      keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rbd_sin_movimiento_${fechaInicio}_${fechaFin}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          RBD SIN MOVIMIENTO DE TRÁFICO
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Todos los RBD que se nombran aquí están con{" "}
          <strong>Estado de servicio: Conectado</strong> y{" "}
          <strong>Estado de pago: Facturando</strong>
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "40vh" }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Cargando...</Typography>
          </Box>
        ) : (
          <>
            {/* Metrics summary */}
            <Paper elevation={0} sx={{ mb: 3, border: "1px solid rgba(0,0,0,0.15)" }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, background: "#e0e0e0" }}>Métricas</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, background: "#e0e0e0" }}>Medida de datos</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, background: SALMON }}>Cantidad de RBD</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {METRICS.map((m, idx) => (
                      <TableRow key={m.key}>
                        <TableCell sx={{ fontSize: "0.85rem" }}>{m.label}</TableCell>
                        <TableCell align="center">{metricas[m.key] ?? 0}</TableCell>
                        {idx === 0 && (
                          <TableCell
                            align="center"
                            rowSpan={METRICS.length}
                            sx={{ background: SALMON, fontWeight: 900, fontSize: "2.5rem", verticalAlign: "middle" }}
                          >
                            {totalRbd.toLocaleString()}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Filters */}
            <Box sx={{ display: "flex", alignItems: "stretch", mb: 3, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 2.5, background: "#fff", gap: 0 }}>
              {/* Date pickers */}
              <Box sx={{ flex: "0 0 auto", pr: 3 }}>
                <Typography variant="body2" sx={{ mb: 0.75 }}>Desde:</Typography>
                <TextField
                  type="date"
                  size="small"
                  value={fechaInicio}
                  onChange={(e) => { setFechaInicio(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: fechaFin }}
                />
                <Typography variant="body2" sx={{ mt: 2.5, mb: 0.75 }}>Hasta:</Typography>
                <TextField
                  type="date"
                  size="small"
                  value={fechaFin}
                  onChange={(e) => { setFechaFin(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: fechaInicio, max: today }}
                />
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mx: 3 }} />

              {/* Agrupar por */}
              <Box sx={{ flex: "0 0 auto", pr: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>Agrupar por:</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={grouping.operador}
                      onChange={(e) => setGrouping((p) => ({ ...p, operador: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body1">Por Operador</Typography>}
                  sx={{ display: "flex", ml: 0, mb: 0.5 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={grouping.zona}
                      onChange={(e) => setGrouping((p) => ({ ...p, zona: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body1">Por Zona</Typography>}
                  sx={{ display: "flex", ml: 0 }}
                />
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mr: 3 }} />

              {/* Extensión en tiempo */}
              <Box sx={{ flex: "1" }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>Extención en tiempo:</Typography>
                <Grid container columnSpacing={1} rowSpacing={0.5}>
                  {TIME_FILTERS.map((f) => (
                    <Grid size={6} key={f.key}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={tiempoActivo === f.key}
                            onChange={(e) => { setTiempoActivo(e.target.checked ? f.key : null); setPage(0); }}
                          />
                        }
                        label={<Typography variant="body1">{f.label}</Typography>}
                        sx={{ ml: 0 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

            {/* Data tables */}
            <PageBgBlock pad="0">
              {isGrouped && filteredDatos.length === 0 ? (
                <Paper elevation={0} sx={{ width: "100%", mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Total RBD: 0</Typography>
                  </Box>
                  <DataTable rows={[]} showPagination={false} />
                </Paper>
              ) : isGrouped ? (
                groups.map((g) => {
                  const title = buildGroupTitle(enabledGroupKeys, g.partsMap);
                  return (
                    <Paper key={g.id} variant="outlined" sx={{ mb: 2 }}>
                      <Box sx={{ px: 2, py: 1.5, background: GROUP_BG, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {title}&nbsp;&nbsp;
                          <Box component="span" sx={{ fontWeight: 400, color: "rgba(0,0,0,0.60)" }}>
                            Total RBD: {g.rows.length.toLocaleString()}
                          </Box>
                        </Typography>
                      </Box>
                      <DataTable rows={g.rows} showPagination={false} />
                    </Paper>
                  );
                })
              ) : (
                <Paper elevation={0} sx={{ width: "100%", mb: 2 }} >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Total RBD: {filteredDatos.length.toLocaleString()}
                    </Typography>
                  </Box>
                  <DataTable
                    rows={pagedDatos}
                    totalCount={filteredDatos.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    showPagination
                  />
                </Paper>
              )}
            </PageBgBlock>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="outlined" onClick={handleDownloadCSV}>
                Descargar detalle (.csv)
              </Button>
            </Box>
          </>
        )}
      </Box>
    </PageBody>
  );
}
